import type { ReactNode } from 'react'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import { StatusChip } from './Chip'
import { useNls } from '../../nls/nlsProvider'
import './settingsKit.css'

export interface SettingsChoiceOption {
  id: string
  label: ReactNode
  description?: ReactNode
  preview?: ReactNode
  locked?: boolean
  rarity?: string
}

export interface SettingsChoiceGridProps {
  options: SettingsChoiceOption[]
  value?: string
  onChange?: (id: string) => void
  onPreviewLocked?: (option: SettingsChoiceOption) => void
  className?: string
  testId?: string
}

/**
 * SettingsChoiceGrid (SR2, §3.3, §3.7)
 * Picture tiles for themes, backgrounds, soundscapes.
 * 3 per row at desktop, 2 at phone width.
 * Active tile: 2px accent ring + checkmark.
 * Locked tile: lock chip + Preview action, blocks onChange.
 */
export function SettingsChoiceGrid({
  options,
  value,
  onChange,
  onPreviewLocked,
  className,
  testId,
}: SettingsChoiceGridProps) {
  const { t } = useNls()

  return (
    <div
      className={['skChoiceGrid', className].filter(Boolean).join(' ')}
      data-testid={testId}
      role="radiogroup"
    >
      {options.map((option) => {
        const isActive = option.id === value
        const isLocked = Boolean(option.locked)

        const handleClick = () => {
          if (isLocked) {
            onPreviewLocked?.(option)
          } else {
            onChange?.(option.id)
          }
        };

        return (
          <div
            key={option.id}
            className={[
              'skChoiceTile',
              isActive ? 'is-active' : '',
              isLocked ? 'is-locked' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={handleClick}
            role="radio"
            aria-checked={isActive}
            tabIndex={0}
            data-testid={`choice-tile-${option.id}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleClick()
              }
            }}
          >
            {option.preview ? (
              <div className="skChoiceTile__preview">
                {option.preview}
              </div>
            ) : null}

            <div className="skChoiceTile__info">
              <div className="skChoiceTile__header">
                <span className="skChoiceTile__label">{option.label}</span>
                {isActive ? (
                  <CheckRoundedIcon
                    fontSize="inherit"
                    style={{ color: 'var(--sk-accent)', fontSize: '18px' }}
                  />
                ) : null}
              </div>
              {option.description ? (
                <span className="skChoiceTile__desc">{option.description}</span>
              ) : null}
            </div>

            {isLocked ? (
              <div className="skChoiceTile__footer">
                <StatusChip
                  tone="neutral"
                  size="sm"
                  icon={<LockRoundedIcon fontSize="inherit" />}
                >
                  {t('NLSSettingsKitLocked')}
                </StatusChip>
                <span
                  className="zapBtn zapBtn--secondary zapBtn--sm"
                  aria-hidden="true"
                >
                  <span className="zapBtn__label">{t('NLSSettingsKitPreview')}</span>
                </span>
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
